import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { supabase as cloudSupabase } from './client';
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_ANON_KEY } from '@/config/externalSupabase';

/**
 * Vendor-specific Supabase client.
 * 
 * Auth → External Supabase with storageKey 'vendor-auth-token'
 * Functions → Lovable Cloud (edge functions are hosted there)
 * Everything else (from, storage, channel, rpc) → External DB
 * 
 * Uses a separate storage key so vendor and driver sessions
 * can coexist on the same browser without conflicts.
 */
const externalVendorClient = createClient<Database>(
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
  {
    auth: {
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'vendor-auth-token',
    },
  }
);

/**
 * Vendor client export:
 * - `.auth`, `.from()`, `.storage`, `.channel()`, `.removeChannel()`, `.rpc()` → external DB
 * - `.functions` → Lovable Cloud (edge functions are hosted there)
 */
export const vendorSupabase = new Proxy(externalVendorClient, {
  get(target, prop, receiver) {
    // Only route functions to Cloud — everything else stays on external
    if (prop === 'functions') {
      return (cloudSupabase as any)[prop];
    }
    return Reflect.get(target, prop, receiver);
  },
}) as SupabaseClient<Database>;
